import { useEffect, useRef, useState } from 'react';
import ModalCloseButton from './ModalCloseButton';
import { FileDown, ExternalLink, Layers } from 'lucide-react';

// PdfModeModal — small chooser shown when the user clicks "Save/View
// PDF" on either the Tax Account Audit or the Year-End Report. Three
// mutually-exclusive modes:
//
//   download → save the PDF to disk (this is the default)
//   newtab   → open the PDF in a new browser tab (no download)
//   both     → do both at once
//
// Apply runs the chosen mode AND closes the modal. Cancel and the
// red ModalCloseButton (top-right, same affordance as the Stock
// Modal) close without doing anything.
//
// Props:
//   open      — boolean
//   onClose   — () => void   (cancel / X)
//   onApply   — (mode) => void | Promise<void>   (Apply button)
//   title     — string, optional. Defaults to "Save / View PDF".

const PDF_MODE_KEYS = ['download', 'newtab', 'both'];
const PDF_MODE_STORAGE_KEY = 'pdf-mode-pref';

// Helper for the page-level "Save/View PDF" buttons. Returns the
// user's previously-saved mode (`download` / `newtab` / `both`) or
// `null` if nothing is saved. Swallows localStorage failures (some
// privacy-mode browsers throw on .getItem()).
export function getSavedPdfMode() {
  try {
    const saved = localStorage.getItem(PDF_MODE_STORAGE_KEY);
    return saved && PDF_MODE_KEYS.includes(saved) ? saved : null;
  } catch {
    return null;
  }
}

// Mirror for tests / programmatic reset.
export function clearSavedPdfMode() {
  try { localStorage.removeItem(PDF_MODE_STORAGE_KEY); } catch { /* ignore */ }
}

const OPTIONS = [
  {
    key: 'newtab',
    label: 'View PDF in a new tab',
    hint: 'Opens the PDF inline in a new browser tab — no file is saved to your computer.',
    Icon: ExternalLink,
  },
  {
    key: 'download',
    label: 'Download PDF',
    hint: 'Saves the PDF directly to your Downloads folder.',
    Icon: FileDown,
  },
  {
    key: 'both',
    label: 'Both',
    hint: 'Saves the PDF AND opens it in a new tab so you can preview it right away.',
    Icon: Layers,
  },
];

export default function PdfModeModal({ open, onClose, onApply, title = 'Save / View PDF' }) {
  const [mode, setMode] = useState('download');
  const [remember, setRemember] = useState(false);
  const [applying, setApplying] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Rehydrate any saved preference. If found, pre-select that
      // mode AND tick the Remember box (since the user previously
      // asked us to remember). If nothing saved, fall back to the
      // safe default — Download — and leave Remember unchecked.
      let saved = null;
      try {
        saved = localStorage.getItem(PDF_MODE_STORAGE_KEY);
      } catch {
        // privacy-mode browsers throw on localStorage access; just
        // behave like there's nothing saved.
      }
      if (saved && PDF_MODE_KEYS.includes(saved)) {
        setMode(saved);
        setRemember(true);
      } else {
        setMode('download');
        setRemember(false);
      }
      setApplying(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !applying) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, applying]);

  if (!open) return null;

  const handleApply = async () => {
    if (applying) return;
    // Persist (or clear) the saved preference based on Remember.
    // Doing this BEFORE the await means even if the PDF render
    // throws, the user's stated preference is still honoured next
    // time they open the modal. localStorage failures (incognito
    // / quota-exceeded) are swallowed silently — not worth a toast.
    try {
      if (remember) {
        localStorage.setItem(PDF_MODE_STORAGE_KEY, mode);
      } else {
        localStorage.removeItem(PDF_MODE_STORAGE_KEY);
      }
    } catch {
      // ignore
    }
    setApplying(true);
    try {
      await onApply?.(mode);
    } finally {
      setApplying(false);
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onMouseDown={(e) => {
        // Click-outside closes (parity with other modals) — but only
        // when we're not mid-Apply, to prevent accidentally cancelling
        // an in-flight render.
        if (applying) return;
        if (wrapRef.current && !wrapRef.current.contains(e.target)) onClose?.();
      }}
      data-testid="pdf-mode-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-mode-modal-title"
    >
      <div
        ref={wrapRef}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-xl shadow-2xl max-w-md w-full p-5"
      >
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="flex-1 min-w-0">
            <h3 id="pdf-mode-modal-title" className="text-base font-bold tracking-tight">
              {title}
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
              Choose how you want your PDF. Apply confirms; Cancel or the red × backs out.
            </p>
          </div>
          <ModalCloseButton onClick={() => { if (!applying) onClose?.(); }} testId="pdf-mode-modal-close" />
        </div>

        <fieldset className="space-y-2 mb-4">
          <legend className="sr-only">PDF delivery mode</legend>
          {OPTIONS.map((opt) => {
            const checked = mode === opt.key;
            const Icon = opt.Icon;
            return (
              <label
                key={opt.key}
                className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${checked
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50 hover:bg-muted/40'}`}
                data-testid={`pdf-mode-option-${opt.key}`}
              >
                <input
                  type="checkbox"
                  name="pdf-mode"
                  value={opt.key}
                  checked={checked}
                  onChange={() => setMode(opt.key)}
                  className="mt-1 accent-primary w-4 h-4"
                  data-testid={`pdf-mode-checkbox-${opt.key}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-semibold">
                    <Icon className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                    <span>{opt.label}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{opt.hint}</p>
                </div>
              </label>
            );
          })}
        </fieldset>

        {/* Round 92o++++++++N+++ — "Remember my choice" lets power
            users skip this modal mentally on future exports. When
            checked + Apply, we persist `mode` to localStorage so the
            next open pre-selects it. Unchecking + Apply clears the
            saved value, restoring the Download default. */}
        <label
          className="flex items-start gap-2 mb-4 p-2 -mx-1 rounded-md cursor-pointer hover:bg-muted/30"
          data-testid="pdf-mode-remember-row"
        >
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="mt-0.5 accent-primary w-4 h-4"
            data-testid="pdf-mode-remember-checkbox"
          />
          <span className="text-[12px] leading-snug">
            <span className="font-semibold">Remember my choice</span>
            <span className="text-muted-foreground">
              {' '}— skip this step next time and run my selected option automatically. You can change it any time by re-opening this modal.
            </span>
          </span>
        </label>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleApply}
            disabled={applying}
            className="flex-1 h-11 px-4 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-bold shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="pdf-mode-apply"
          >
            {applying ? 'Working…' : 'Apply'}
          </button>
          <button
            type="button"
            onClick={() => { if (!applying) onClose?.(); }}
            disabled={applying}
            className="h-11 px-4 rounded-lg border bg-card hover:bg-muted text-sm font-semibold transition-colors disabled:opacity-60"
            data-testid="pdf-mode-cancel"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
